<template>
  <ButtonV2
    variant="subtle"
    size="md"
    class="max-large-desktop:@container gap-2 flex-1 line-clamp-1 break-good-singleline text-ellipsis"
    :class="{
      'flex !justify-start text-start !px-2 min-h-11 !py-1': type === 'simple',
      'px-6': type === 'with-balances',
      'rounded-xl': type === 'simple' && !extended,
      'rounded-none w-full': extended,
      'max-w-fit': !extended,
    }"
    :style="{
      'min-height': connectWalletButtonHeight,
    }"
    v-bind="$attrs"
    @click="connectWallet"
  >
    <Icon
      icon="material-symbols:account-balance-wallet"
      class="text-xl shrink-0"
    />
    <span
      class="break-good-singleline text-ellipsis uppercase font-serif @[250px]:!line-clamp-1 max-desktop:hidden desktop:line-clamp-1"
      :class="{
        'text-sm': type === 'simple',
        'text-base': type === 'with-balances',
      }"
    >
      <template v-if="isWalletConnected && truncatedAddress">
        {{ truncatedAddress }}
      </template>
      <template v-else>
        {{
          $t(
            isWalletConnected
              ? "channel.auth.webThree.walletConnected"
              : "channel.auth.webThree.connectWallet",
          )
        }}
      </template>
    </span>
    <Icon
      v-if="isWalletConnected"
      icon="material-symbols:check-circle-rounded"
      class="text-fill-success size-4 shrink-0"
      :class="{
        'ml-auto': requestedTokens.length === 0,
      }"
    />

    <span
      v-if="isWalletConnected && requestedTokens.length > 0"
      class="@[250px]:ml-auto inline space-x-2 items-center max-w-full max-h-5 overflow-hidden whitespace-normal text-start shrink-0"
    >
      <span v-if="!extended" class="inline-block bg-border-brand w-1u h-9u" />
      <span
        v-for="token in requestedTokens"
        :key="token.address"
        class="gap-0.5 text-xs text-text-secondary font-medium whitespace-nowrap"
      >
        <span>{{
          `${$t("channel.auth.webThree.availableTokenLabel", {
            tokenLabel: token.label,
          })}: `
        }}</span>
        <span>{{ token.amount }}</span>
      </span>
      <template v-if="extended">
        <span class="text-text-secondary text-xs ml-2.5 font-medium">{{
          $t("channel.auth.webThree.seeMore")
        }}</span>
        <span v-if="extended" class="inline-block align-middle">
          <Icon
            icon="mdi:arrow-right"
            class="size-4 text-brand-icon-emphasis"
          />
        </span>
      </template>
    </span>
  </ButtonV2>
</template>

<script lang="ts">
export const connectWalletButtonHeight = "2.5625rem";
</script>

<script setup lang="ts">
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { Icon } from "@iconify/vue";
import { type WebThreeTokenWithBalance } from "@/constants/web-three";
import { useAuthForAction } from "@/composables/global/use-auth-for-action";
import type {
  AuthorizationRuleGroupForGating,
  UnnormalizedAuthorizationRuleGroupForGatingType,
} from "@/features/gating/use-gating-permissions";
import { useUserStore } from "@/stores/user.store";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import { useWalletService } from "@/services/wallet/wallet.service";
import { GatingStrategyType } from "@/features/gating/strategies/gating-strategy";
const props = defineProps<{
  type?: "simple" | "with-balances";
  extended?: boolean;
  showAvailableTokens?: boolean;
  authorizationRuleGroup?: MaybeRefOrGetter<UnnormalizedAuthorizationRuleGroupForGatingType>;
}>();

const walletService = useWalletService();
const { emitChannelEvent } = useChannelEvents();
const userStore = useUserStore();
const trackingService = useTrackingStore();

const isWalletConnected = computed(() => walletService.isConnected);

const truncatedAddress = computed(() => {
  const address = walletService.walletAddress;
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
});

const connectWallet = useAuthForAction(async () => {
  if (!isWalletConnected.value) {
    trackingService.track({
      event: TrackEventEnum.ConnectWalletTriggered,
      category: EventCategory.Identity,
      metadata: {},
    });
    userStore.trackAnalyticsAction({
      appActionName: "CONNECT_WALLET_INTENT",
    });
    await walletService.connect();
  } else {
    emitChannelEvent({
      type: ChannelEventType.SHOW_UPGRADE_MODAL,
      gatingStrategyType: GatingStrategyType.token,
    });
  }
});

const requestedTokens = computed(() => {
  const tokens: Record<string, WebThreeTokenWithBalance> = {};

  if (props.showAvailableTokens && walletService.tokenBalanceMap) {
    for (const token of walletService.tokenBalanceMap.values()) {
      if (!token) continue;

      tokens[token.address] = token;
    }
  } else {
    const group = toValue(props.authorizationRuleGroup) as
      | AuthorizationRuleGroupForGating
      | null
      | undefined;
    if (group) {
      for (const rule of group.authorization_rules) {
        const tokenAddress =
          rule.web_three_token_criterion?.web_three_erc_twenty_token.address;

        if (!tokenAddress || tokenAddress in tokens) continue;

        const token = walletService.tokenBalanceMap.get(tokenAddress);

        if (!token) continue;
        tokens[tokenAddress] = token;
      }
    }
  }

  return Object.values(tokens)
    .sort((a, b) => {
      return b.amount - a.amount;
    })
    .slice(0, 3);
});
</script>
